/**
 * @description Build invoice/estimate "From" merchant display from Company Settings.
 * Company Settings is the single source of truth; form edits are per-document
 * overrides and do not update Company Settings.
 *
 * @param {Object|null|undefined} company - Company from AuthContext / useCompany
 * @param {Object|null|undefined} user - Current user (email fallback only)
 * @returns {{
 *   displayName: string,
 *   displayAddress: string,
 *   displayCity: string,
 *   displayState: string,
 *   displayZip: string,
 *   displayEmail: string,
 *   displayPhone: string,
 *   displayWebsite: string,
 *   logoUrl: null
 * }}
 */
export function buildMerchantInfoFromCompany(company, user) {
  return {
    displayName: company?.businessName || '',
    displayAddress: company?.addressLine1 || '',
    displayCity: company?.city || '',
    displayState: company?.state || '',
    displayZip: company?.postalCode || '',
    // Prefer company email; user.email is only an interim until Company Settings is filled
    displayEmail: company?.email || user?.email || '',
    displayPhone: company?.phone || '',
    displayWebsite: company?.website || '',
    logoUrl: null
  };
}

/**
 * Unambiguous legacy contact / street placeholders previously hardcoded in create forms.
 * Do NOT include bare City / State / 12345 — those are real values (see LESSONS scrub rule).
 */
export const MERCHANT_PLACEHOLDER_VALUES = new Set([
  'Your Business Name',
  '123 Business St',
  '123 Business Street',
  'email@business.com',
  '(555) 123-4567',
  '555-123-4567',
  '5551234567',
  'www.yourbusiness.com',
  'http://www.yourbusiness.com',
  'https://www.yourbusiness.com'
]);

/**
 * Treat known placeholders as empty so company defaults can apply.
 * Address city/state/zip are only scrubbed when the street was the known fake default.
 *
 * @param {string|null|undefined} value
 * @param {{ street?: string|null }} [context]
 * @returns {string}
 */
export function scrubMerchantPlaceholder(value, context = {}) {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (MERCHANT_PLACEHOLDER_VALUES.has(trimmed)) return '';

  const street = context.street == null ? '' : String(context.street).trim();
  const fakeStreet = street === '123 Business St' || street === '123 Business Street'
    || MERCHANT_PLACEHOLDER_VALUES.has(street);
  if (fakeStreet && (trimmed === 'City' || trimmed === 'State' || trimmed === '12345')) {
    return '';
  }
  return trimmed;
}

/**
 * Stable fingerprint of Company Settings fields used for invoice/estimate From.
 * When this changes after a Settings save, create forms should re-seed unless the
 * merchant already opened the per-doc From editor.
 *
 * @param {Object|null|undefined} company
 * @returns {string}
 */
export function getCompanyContactKey(company) {
  if (!company) return '';
  return [
    company.id ?? '',
    company.businessName ?? '',
    company.addressLine1 ?? '',
    company.addressLine2 ?? '',
    company.city ?? '',
    company.state ?? '',
    company.postalCode ?? '',
    company.phone ?? '',
    company.email ?? '',
    company.website ?? ''
  ].join('\u0001');
}

/**
 * Fill empty merchant From fields from company without clobbering real edits.
 * Also upgrades interim user.email to company.email once Company Settings has one.
 *
 * @param {Object} prev
 * @param {Object|null|undefined} company
 * @param {Object|null|undefined} user
 * @returns {Object}
 */
export function fillEmptyMerchantInfoFromCompany(prev, company, user) {
  const defaults = buildMerchantInfoFromCompany(company, user);
  const interimUserEmail = Boolean(
    user?.email
    && prev.displayEmail === user.email
    && company?.email
    && company.email !== user.email
  );

  return {
    displayName: prev.displayName || defaults.displayName,
    displayAddress: prev.displayAddress || defaults.displayAddress,
    displayCity: prev.displayCity || defaults.displayCity,
    displayState: prev.displayState || defaults.displayState,
    displayZip: prev.displayZip || defaults.displayZip,
    displayEmail: interimUserEmail
      ? company.email
      : (prev.displayEmail || defaults.displayEmail),
    displayPhone: prev.displayPhone || defaults.displayPhone,
    displayWebsite: prev.displayWebsite || defaults.displayWebsite,
    logoUrl: prev.logoUrl ?? defaults.logoUrl
  };
}
